import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/ui/button';
import { QuoteWidget } from '@/components/marketing/QuoteWidget';
import { Zap, Check, Shield, Clock, DollarSign, TrendingUp } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { motion } from 'framer-motion';

export function PricingSection() {
  return (
    <Section
      id="pricing"
      eyebrow="Crystal Clear Pricing"
      title="Transparent pricing that makes sense. Finally."
      subtitle="See the exact price upfront. No hidden fees, no surprise charges, no seasonal BS. Just honest pricing for honest work."
      background="frost"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-16">
        {/* On-Demand */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card variant="solid" className="h-full hover:shadow-card-hover transition-all duration-300 group">
            <div className="flex items-start justify-between mb-6">
              <Badge variant="accent" className="text-sm">PAY-PER-STORM</Badge>
              <div className="text-right">
                <p className="text-sm font-bold text-neutral-600 uppercase tracking-wider mb-1">From</p>
                <p className="text-5xl font-black gradient-text">$30</p>
                <p className="text-sm font-semibold text-neutral-600">per clear</p>
              </div>
            </div>

            <h3 className="text-3xl font-black mb-4 leading-tight">
              Pay only when it snows.<br />
              <span className="text-brand-500">Zero commitment.</span>
            </h3>

            <p className="text-neutral-700 font-medium mb-6 leading-relaxed">
              Perfect for homeowners who want flexibility. Book as you need it—cancel anytime before we match you with an operator.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl group-hover:bg-brand-50 transition-colors">
                <Check className="h-6 w-6 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                <div>
                  <p className="font-bold text-neutral-900">See your price first</p>
                  <p className="text-sm text-neutral-600">Instant quote before you book—no guessing games</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl group-hover:bg-brand-50 transition-colors">
                <Check className="h-6 w-6 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                <div>
                  <p className="font-bold text-neutral-900">Free cancellation</p>
                  <p className="text-sm text-neutral-600">Cancel before operator assignment, $0 charged</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-neutral-50 rounded-xl group-hover:bg-brand-50 transition-colors">
                <Check className="h-6 w-6 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={3} />
                <div>
                  <p className="font-bold text-neutral-900">Add-ons priced upfront</p>
                  <p className="text-sm text-neutral-600">Walkways, stairs, sidewalks—clear pricing for extras</p>
                </div>
              </div>
            </div>

            <QuoteWidget />
          </Card>
        </motion.div>

        {/* Storm Pass */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card variant="outline" className="h-full border-3 border-accent-500 bg-gradient-to-br from-accent-500/5 via-white to-brand-500/5 hover:shadow-glow-green transition-all duration-300 relative overflow-hidden group">
            {/* Animated Shimmer Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            
            <div className="relative z-10">
              <div className="flex items-start justify-between mb-6">
                <div className="flex flex-col gap-2">
                  <Badge variant="accent" className="text-sm w-fit">PREMIUM MEMBERSHIP</Badge>
                  <div className="flex items-center gap-2 text-accent-600">
                    <Zap className="w-5 h-5" fill="currentColor" />
                    <span className="text-xs font-black uppercase tracking-widest">Limited Spots</span>
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-black mb-4 leading-tight">
                Storm Pass:<br />
                <span className="gradient-text">Never shovel again.</span>
              </h3>

              <p className="text-neutral-800 font-bold mb-8 leading-relaxed text-lg">
                Set it and forget it. Auto-clear every storm with priority matching and locked-in rates. This is how pros handle winter.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-accent-500/20">
                  <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-white" fill="currentColor" />
                  </div>
                  <div>
                    <p className="font-black text-neutral-900">Priority operator matching</p>
                    <p className="text-sm text-neutral-700 font-medium">First in line when storms hit—guaranteed service</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-accent-500/20">
                  <div className="w-10 h-10 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-neutral-900">Auto-book on snowfall</p>
                    <p className="text-sm text-neutral-700 font-medium">Set your threshold, we handle the rest automatically</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-accent-500/20">
                  <div className="w-10 h-10 rounded-full bg-neutral-900 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-neutral-900">Locked-in pricing</p>
                    <p className="text-sm text-neutral-700 font-medium">No surge pricing, ever. Lock your rate for the season</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 border-accent-500/20">
                  <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-black text-neutral-900">Premium support</p>
                    <p className="text-sm text-neutral-700 font-medium">Direct phone line to our team, 24/7 storm season</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-brand-500 to-accent-500 p-[2px] rounded-2xl">
                <Button 
                  variant="primary" 
                  size="lg" 
                  className="w-full bg-white hover:bg-neutral-50 !text-brand-600 hover:!text-brand-700 text-lg font-black py-6 rounded-2xl shadow-none group/btn"
                  onClick={() => {
                    trackEvent('storm_pass_waitlist_click');
                    window.location.href = '/storm-pass';
                  }}
                >
                  <span className="flex items-center gap-2">
                    Join Waitlist — Reserve Your Spot
                    <motion.span
                      animate={{ x: [0, 4, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                  </span>
                </Button>
              </div>

              <p className="text-center text-sm text-neutral-600 font-semibold mt-4">
                🔥 <strong className="text-accent-600">86 spots</strong> claimed in your area this week
              </p>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Trust Bar */}
      <motion.div 
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="text-center">
          <DollarSign className="w-10 h-10 text-accent-500 mx-auto mb-3" />
          <p className="text-2xl font-black text-neutral-900 mb-1">$0 Hidden Fees</p>
          <p className="text-sm text-neutral-600 font-medium">What you see is what you pay. Always.</p>
        </div>
        <div className="text-center">
          <Shield className="w-10 h-10 text-brand-500 mx-auto mb-3" />
          <p className="text-2xl font-black text-neutral-900 mb-1">100% Verified</p>
          <p className="text-sm text-neutral-600 font-medium">Licensed, insured, background-checked operators</p>
        </div>
        <div className="text-center">
          <Check className="w-10 h-10 text-accent-500 mx-auto mb-3" strokeWidth={3} />
          <p className="text-2xl font-black text-neutral-900 mb-1">Money-Back</p>
          <p className="text-sm text-neutral-600 font-medium">Job not done right? We'll make it right, Snowfund or refund.</p>
        </div>
      </motion.div>
    </Section>
  );
}

